import {
  COMMERCIAL_DOMAINS,
  COMMERCIAL_DOMAIN_LIST,
  COMMERCIAL_DOMAIN_SCHEMAS,
  computeCircuitId,
  emptyCommercialAcfGroups,
  normalizeDepartments,
  normalizeMulti,
  serviceSpeedSlug,
} from "./commercialProjectSchema";
import { applyCommercialSubphasesToAcf } from "./commercialSubphases";

const LEGACY_SUMMARY_KEYS = ["Project Summary Test 2", "project_summary_test_2"];
const LEGACY_DOMAIN_PREFIXES = [
  ["engineering_details", ["Engineering Test 2", "engineering_test_2"]],
  ["construction_details", ["Construction Test 2", "construction_test_2"]],
  ["fiber_details", ["Fiber Test 2", "fiber_test_2"]],
  ["networking_details", ["Networking Test 2", "networking_test_2"]],
  ["accounting_details", ["accounting_test_2", "Accounting Test 2"]],
];

function isPlainObject(v) {
  return v != null && typeof v === "object" && !Array.isArray(v);
}

/** Convert numeric-key repeater objects to arrays. */
export function repeaterToArray(v) {
  if (Array.isArray(v)) return v.map((row) => (isPlainObject(row) ? { ...row } : row));
  if (!isPlainObject(v)) return [];
  const keys = Object.keys(v).sort((a, b) => Number(a) - Number(b));
  return keys.map((k) => ({ ...v[k] }));
}

export function repeaterToObject(arr) {
  if (!Array.isArray(arr)) return {};
  const out = {};
  arr.forEach((row, i) => {
    out[String(i)] = row;
  });
  return out;
}

function normalizePermitMaterialsForStorage(materials) {
  const rows = repeaterToArray(materials)
    .map((row) => {
      const r = row && typeof row === "object" ? row : {};
      const item = r.item != null ? String(r.item).trim() : "";
      const slug = r.slug != null ? String(r.slug).trim() : "";
      const quantity = r.quantity != null ? String(r.quantity).trim() : "";
      if (!item && !slug && !quantity) return null;
      const out = {};
      if (slug) out.slug = slug;
      if (item) out.item = item;
      if (quantity) out.quantity = quantity;
      return out;
    })
    .filter(Boolean);
  return rows.length ? repeaterToObject(rows) : undefined;
}

/** Persist engineering permits in ACF numeric-key repeater shape. */
export function normalizeEngineeringPermitsForStorage(permits) {
  const rows = repeaterToArray(permits);
  if (!rows.length) return undefined;
  const normalized = rows.map((row) => {
    const out = { ...row };
    const riders = repeaterToArray(row?.riders);
    if (riders.length) out.riders = repeaterToObject(riders);
    else delete out.riders;
    if (row?.materials != null) {
      const stored = normalizePermitMaterialsForStorage(row.materials);
      if (stored) out.materials = stored;
      else delete out.materials;
    }
    return out;
  });
  return repeaterToObject(normalized);
}

function legacyPermitFilesToPermits(permitFiles) {
  const rows = repeaterToArray(permitFiles);
  if (!rows.length) return undefined;
  const mapped = rows
    .map((row) => {
      const out = {
        agency: row?.agency ?? row?.permit_agency,
        permit_plan: row?.permit_plan,
        permit_application: row?.permit_application,
        application_submitted_date: row?.application_submitted_date,
        permit: row?.permit,
        permit_received_date: row?.permit_received_date,
        permit_expiration_date: row?.permit_expiration_date ?? row?.expiration_date,
      };
      return Object.values(out).some((v) => v != null && String(v).trim() !== "") ? out : null;
    })
    .filter(Boolean);
  return mapped.length ? mapped : undefined;
}

function liftEngineeringPermitsFromLegacy(acfJson, engineeringSlice) {
  const eng = isPlainObject(engineeringSlice) ? { ...engineeringSlice } : {};
  if (repeaterToArray(eng.permits).length) return eng;

  const fromPermitFiles = legacyPermitFilesToPermits(eng.permit_files);
  if (fromPermitFiles) {
    eng.permits = fromPermitFiles;
    return eng;
  }

  for (const lk of ["Engineering Test 2", "engineering_test_2"]) {
    const leg = acfJson?.[lk];
    if (!isPlainObject(leg)) continue;
    const legacyPermits = repeaterToArray(leg.permits);
    if (legacyPermits.length) {
      return { ...eng, ...leg, permits: leg.permits };
    }
    const converted = legacyPermitFilesToPermits(leg.permit_files);
    if (converted) {
      return { ...eng, ...leg, permits: converted };
    }
  }
  return eng;
}

function liftLegacySummary(group) {
  if (!isPlainObject(group)) return {};
  const out = { ...group };
  if (out.customer_name != null && out.customer == null) out.customer = out.customer_name;
  if (out.title != null && out.project == null) out.project = out.title;
  const fromDepartments = normalizeDepartments(out.departments);
  const fromLegacy = normalizeDepartments(out.department_involvement);
  const merged = fromDepartments.length ? fromDepartments : fromLegacy;
  // normalizeDepartments already canonicalizes via canonicalizeDepartmentList
  if (merged.length) {
    out.departments = merged;
    out.department_involvement = merged;
  }
  return out;
}

export function readCommercialDomains(acfGroupsJson) {
  const j = isPlainObject(acfGroupsJson) ? acfGroupsJson : {};
  const out = emptyCommercialAcfGroups();

  if (isPlainObject(j[COMMERCIAL_DOMAINS.SUMMARY])) {
    out[COMMERCIAL_DOMAINS.SUMMARY] = liftLegacySummary(j[COMMERCIAL_DOMAINS.SUMMARY]);
  } else {
    for (const k of LEGACY_SUMMARY_KEYS) {
      if (isPlainObject(j[k])) {
        out[COMMERCIAL_DOMAINS.SUMMARY] = liftLegacySummary(j[k]);
        break;
      }
    }
  }

  for (const [domainKey, legacyKeys] of LEGACY_DOMAIN_PREFIXES) {
    if (isPlainObject(j[domainKey])) {
      let slice = { ...j[domainKey] };
      if (domainKey === COMMERCIAL_DOMAINS.ENGINEERING) {
        slice = liftEngineeringPermitsFromLegacy(j, slice);
      }
      out[domainKey] = slice;
      continue;
    }
    for (const lk of legacyKeys) {
      if (isPlainObject(j[lk])) {
        let slice = { ...j[lk] };
        if (domainKey === COMMERCIAL_DOMAINS.ENGINEERING) {
          slice = liftEngineeringPermitsFromLegacy(j, slice);
        }
        out[domainKey] = slice;
        break;
      }
    }
  }

  const eng = out[COMMERCIAL_DOMAINS.ENGINEERING];
  if (eng?.permits) {
    eng.permits = repeaterToArray(eng.permits).map((row) =>
      row?.materials == null ? row : { ...row, materials: repeaterToArray(row.materials) }
    );
  }
  const fib = out[COMMERCIAL_DOMAINS.FIBER];
  if (fib?.fiber_files) fib.fiber_files = repeaterToArray(fib.fiber_files);
  const net = out[COMMERCIAL_DOMAINS.NETWORKING];
  if (net?.installations) net.installations = repeaterToArray(net.installations);
  out[COMMERCIAL_DOMAINS.NETWORKING] = {
    ...net,
    circuit_id: computeCircuitId(out[COMMERCIAL_DOMAINS.SUMMARY], net),
  };

  return out;
}

/**
 * Read commercial domains from `acf_groups_json`, merged with API `acf_domains` slices
 * (sync table can hold fields missing from the JSON column).
 */
function liftIncidentFlagFromProjectRow(summary, projectRow) {
  if (!isPlainObject(summary) || !projectRow) return summary;
  if (summary.incident != null && summary.incident !== "") return summary;
  if (projectRow.incident == null) return summary;
  return { ...summary, incident: Boolean(projectRow.incident) };
}

export function readCommercialDomainsForRow(projectRow, acfDomains) {
  const fromJson = readCommercialDomains(projectRow?.acf_groups_json);
  let out = { ...fromJson };
  if (acfDomains && typeof acfDomains === "object") {
    for (const domainKey of COMMERCIAL_DOMAIN_LIST) {
      const slice = acfDomains[domainKey];
      if (!isPlainObject(slice)) continue;
      if (domainKey === COMMERCIAL_DOMAINS.SUMMARY) {
        out[domainKey] = liftLegacySummary({ ...(out[domainKey] || {}), ...slice });
      } else {
        out[domainKey] = { ...(out[domainKey] || {}), ...slice };
      }
    }
  }

  const summary = out[COMMERCIAL_DOMAINS.SUMMARY] || {};
  for (const legacyKey of LEGACY_SUMMARY_KEYS) {
    const leg = projectRow?.acf_groups_json?.[legacyKey];
    if (!isPlainObject(leg)) continue;
    const lifted = liftLegacySummary(leg);
    const legDeps = normalizeDepartments(lifted.departments);
    const curDeps = normalizeDepartments(summary.departments);
    if (!curDeps.length && legDeps.length) {
      out[COMMERCIAL_DOMAINS.SUMMARY] = {
        ...summary,
        departments: legDeps,
        department_involvement: legDeps,
      };
      break;
    }
  }

  out[COMMERCIAL_DOMAINS.SUMMARY] = liftIncidentFlagFromProjectRow(
    out[COMMERCIAL_DOMAINS.SUMMARY] || {},
    projectRow
  );

  const summaryAfterIncident = out[COMMERCIAL_DOMAINS.SUMMARY] || {};
  const acfNotes =
    summaryAfterIncident.notes != null && String(summaryAfterIncident.notes).trim() !== ""
      ? String(summaryAfterIncident.notes).trim()
      : "";
  const rowNotes =
    projectRow?.project_notes != null && String(projectRow.project_notes).trim() !== ""
      ? String(projectRow.project_notes).trim()
      : "";
  if (!acfNotes && rowNotes) {
    out[COMMERCIAL_DOMAINS.SUMMARY] = { ...summaryAfterIncident, notes: rowNotes };
  }
  if (
    summaryAfterIncident.new_customer == null &&
    (projectRow?.new_customer === true || projectRow?.new_customer === false)
  ) {
    out[COMMERCIAL_DOMAINS.SUMMARY] = {
      ...(out[COMMERCIAL_DOMAINS.SUMMARY] || {}),
      new_customer: Boolean(projectRow.new_customer),
    };
  }

  const eng = out[COMMERCIAL_DOMAINS.ENGINEERING];
  if (eng?.permits) {
    eng.permits = repeaterToArray(eng.permits).map((row) =>
      row?.materials == null ? row : { ...row, materials: repeaterToArray(row.materials) }
    );
  }
  const fib = out[COMMERCIAL_DOMAINS.FIBER];
  if (fib?.fiber_files) fib.fiber_files = repeaterToArray(fib.fiber_files);
  const net = out[COMMERCIAL_DOMAINS.NETWORKING];
  if (net?.installations) net.installations = repeaterToArray(net.installations);
  out[COMMERCIAL_DOMAINS.NETWORKING] = {
    ...net,
    circuit_id: computeCircuitId(out[COMMERCIAL_DOMAINS.SUMMARY], net),
  };

  return out;
}

/** Keep service speed aligned on summary and networking (import + legacy data). */
export function syncServiceSpeedAcrossCommercialDomains(domains) {
  const summary = domains[COMMERCIAL_DOMAINS.SUMMARY] || {};
  const net = domains[COMMERCIAL_DOMAINS.NETWORKING] || {};
  const raw = [net.service_speed, summary.service_speed].find(
    (v) => v != null && String(v).trim() !== ""
  );
  if (raw == null) return domains;
  const slug = serviceSpeedSlug(raw);
  if (!slug) return domains;
  return {
    ...domains,
    [COMMERCIAL_DOMAINS.SUMMARY]: { ...summary, service_speed: slug },
    [COMMERCIAL_DOMAINS.NETWORKING]: { ...net, service_speed: slug },
  };
}

/** Merge imported CSV fields into existing commercial domains without wiping unmapped keys. */
export function mergeCommercialDomainsPartial(baseDomains, patchDomains) {
  const out = emptyCommercialAcfGroups();
  for (const k of COMMERCIAL_DOMAIN_LIST) {
    out[k] = {
      ...(isPlainObject(baseDomains?.[k]) ? baseDomains[k] : {}),
      ...(isPlainObject(patchDomains?.[k]) ? patchDomains[k] : {}),
    };
    if (k === COMMERCIAL_DOMAINS.ENGINEERING && patchDomains?.[k]?.permits !== undefined) {
      const stored = normalizeEngineeringPermitsForStorage(patchDomains[k].permits);
      if (stored) out[k].permits = stored;
      else delete out[k].permits;
    }
  }
  return out;
}

export function buildCommercialAcfGroupsPatch(domains) {
  const patch = emptyCommercialAcfGroups();
  for (const k of COMMERCIAL_DOMAIN_LIST) {
    const slice = domains[k] && typeof domains[k] === "object" ? { ...domains[k] } : {};
    if (k === COMMERCIAL_DOMAINS.SUMMARY) {
      const deps = normalizeDepartments(slice.departments);
      const fromInvolvement = normalizeDepartments(slice.department_involvement);
      const mergedDeps = deps.length ? deps : fromInvolvement;
      if (mergedDeps.length) {
        slice.departments = mergedDeps;
        slice.department_involvement = mergedDeps;
      }
      if (slice.foc_date != null && String(slice.foc_date).trim() !== "") {
        const iso = normalizeDateToIso(slice.foc_date);
        if (iso) slice.foc_date = iso;
      }
    }
    if (k === COMMERCIAL_DOMAINS.ENGINEERING && slice.permits != null) {
      const stored = normalizeEngineeringPermitsForStorage(slice.permits);
      if (stored) slice.permits = stored;
      else delete slice.permits;
    }
    if (k === COMMERCIAL_DOMAINS.FIBER && Array.isArray(slice.fiber_files)) {
      slice.fiber_files = repeaterToArray(slice.fiber_files);
    }
    if (k === COMMERCIAL_DOMAINS.NETWORKING) {
      slice.circuit_id = computeCircuitId(domains[COMMERCIAL_DOMAINS.SUMMARY], slice);
    }
    if (k === COMMERCIAL_DOMAINS.ACCOUNTING) {
      const mrc = parseCommercialMoney(slice.mrc);
      if (mrc != null) slice.mrc = mrc;
      const nrc = parseCommercialMoney(slice.nrc);
      if (nrc != null) slice.nrc = nrc;
      if (slice.invoiced != null && String(slice.invoiced).trim() !== "") {
        const iso = normalizeDateToIso(slice.invoiced);
        if (iso) slice.invoiced = iso;
      }
    }
    patch[k] = slice;
  }
  return patch;
}

/** Merge commercial domains into existing acf_groups_json (drops legacy test keys when commercial present). */
export function mergeCommercialIntoAcfGroups(existing, domains) {
  const base = isPlainObject(existing) ? { ...existing } : {};
  const commercial = buildCommercialAcfGroupsPatch(domains);
  for (const k of COMMERCIAL_DOMAIN_LIST) {
    base[k] = commercial[k];
  }
  return base;
}

export function commercialDomainsToAcfDomains(domains) {
  const out = {};
  for (const k of COMMERCIAL_DOMAIN_LIST) {
    out[k] = domains[k] || {};
  }
  return out;
}

/** Scalar project row fields synced from commercial domains (grid columns). */
export function projectScalarsFromCommercialDomains(domains) {
  const s = domains[COMMERCIAL_DOMAINS.SUMMARY] || {};
  const a = domains[COMMERCIAL_DOMAINS.ACCOUNTING] || {};
  const subtype = normalizeMulti(s.subtype);
  return {
    work_order: s.work_order != null && String(s.work_order).trim() !== "" ? String(s.work_order).trim() : null,
    customer: s.customer != null && String(s.customer).trim() !== "" ? String(s.customer).trim() : null,
    name: s.project != null && String(s.project).trim() !== "" ? String(s.project).trim() : null,
    project_type: s.type != null && String(s.type).trim() !== "" ? String(s.type).trim() : null,
    subtype: subtype.length ? subtype.join(", ") : null,
    status: s.status != null && String(s.status).trim() !== "" ? String(s.status).trim() : null,
    phase_label: s.phase != null && String(s.phase).trim() !== "" ? String(s.phase).trim() : null,
    foc_date: normalizeDateToIso(s.foc_date) || null,
    sales_rep: s.sales_rep != null && String(s.sales_rep).trim() !== "" ? String(s.sales_rep).trim() : null,
    site_walk: s.site_walk === true || s.site_walk === "true" || s.site_walk === 1,
    service_location:
      s.service_location != null && String(s.service_location).trim() !== ""
        ? String(s.service_location).trim()
        : null,
    a_location: s.a_location != null && String(s.a_location).trim() !== "" ? String(s.a_location).trim() : null,
    z_location: s.z_location != null && String(s.z_location).trim() !== "" ? String(s.z_location).trim() : null,
    new_customer: s.new_customer === true || s.new_customer === "true" || s.new_customer === 1,
    incident: s.incident === true || s.incident === "true" || s.incident === 1,
    ...(Object.prototype.hasOwnProperty.call(s, "notes")
      ? {
          project_notes:
            s.notes != null && String(s.notes).trim() !== "" ? String(s.notes).trim() : null,
        }
      : {}),
    mrc: parseCommercialMoney(a.mrc),
    non_recurring_charge: parseCommercialMoney(a.nrc),
    term_in_months: a.term != null && a.term !== "" ? parseInt(String(a.term), 10) : null,
    invoiced_date: normalizeDateToIso(a.invoiced) || null,
    cvin_expansion: false,
  };
}

function normalizeDateToIso(v) {
  if (v === null || v === undefined) return null;
  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    return v.toISOString().slice(0, 10);
  }
  const s = String(v).trim();
  if (!s) return null;
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const us = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (us) {
    return `${us[3]}-${String(us[1]).padStart(2, "0")}-${String(us[2]).padStart(2, "0")}`;
  }
  return null;
}

function parseCommercialMoney(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(String(v).replace(/[$,]/g, "").trim());
  return Number.isFinite(n) ? n : null;
}

function normalizeCompareValue(v) {
  if (v === null || v === undefined || v === "") return "";
  if (typeof v === "boolean") return v ? "1" : "0";
  if (typeof v === "number") {
    return Number.isFinite(v) ? String(v) : "";
  }
  const dateIso = normalizeDateToIso(v);
  if (dateIso) return `date:${dateIso}`;
  if (Array.isArray(v)) {
    return JSON.stringify(
      v.map((x) => String(x ?? "").trim()).filter(Boolean).sort()
    );
  }
  if (typeof v === "object") {
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }
  const s = String(v).trim();
  const money = parseCommercialMoney(s);
  if (money != null && /^[$,.\d]+$/.test(s.replace(/\s/g, ""))) {
    return `num:${money}`;
  }
  return s;
}

function countRepeaterDiff(saved, draft) {
  const sa = repeaterToArray(saved);
  const da = repeaterToArray(draft);
  if (sa.length !== da.length) return 1;
  return normalizeCompareValue(sa) === normalizeCompareValue(da) ? 0 : 1;
}

function fieldValueChanged(saved, draft, field) {
  if (field.type === "repeater") return countRepeaterDiff(saved, draft) > 0;
  return normalizeCompareValue(saved) !== normalizeCompareValue(draft);
}

function serializeCommercialFieldValue(v) {
  if (v === null || v === undefined || v === "") return "";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) {
    return v
      .map((x) => String(x ?? "").trim())
      .filter(Boolean)
      .join(", ");
  }
  if (typeof v === "object") {
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }
  return String(v).trim();
}

const CHANGE_LOG_ACF_GROUP = "Change Log";

function wpUserStubFromEmail(email) {
  const s = String(email || "").trim();
  if (!s) return "unknown";
  const at = s.indexOf("@");
  return at > 0 ? s.slice(0, at) : s;
}

function formatWpChangeLogDate(d) {
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getMonth() + 1)}/${p(d.getDate())}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

function safeWpInnerSnippet(v) {
  return serializeCommercialFieldValue(v).replace(/'/g, "\u2032");
}

function iterCommercialFieldDiffs(savedDomains, draftDomains, restrictDomains) {
  if (!savedDomains || !draftDomains) return [];
  const domainKeys = restrictDomains?.length
    ? COMMERCIAL_DOMAIN_LIST.filter((k) => restrictDomains.includes(k))
    : COMMERCIAL_DOMAIN_LIST;
  const changes = [];
  for (const domainKey of domainKeys) {
    const schema = COMMERCIAL_DOMAIN_SCHEMAS[domainKey];
    const saved = savedDomains[domainKey] || {};
    const draft = draftDomains[domainKey] || {};
    if (!schema?.fields) continue;
    for (const field of schema.fields) {
      if (field.valueDomain && field.valueDomain !== domainKey) continue;
      if (field.computed || field.type === "readonly") continue;
      if (!fieldValueChanged(saved[field.key], draft[field.key], field)) continue;
      changes.push({
        fieldName: field.label || field.key,
        oldRaw: saved[field.key],
        newVal: draft[field.key],
      });
    }
  }
  return changes;
}

/** Count field-level differences between saved and draft commercial domains. */
export function countCommercialPendingEdits(savedDomains, draftDomains) {
  return iterCommercialFieldDiffs(savedDomains, draftDomains).length;
}

/** List field-level differences between saved and draft commercial domains. */
export function listCommercialFieldChanges(savedDomains, draftDomains, options = {}) {
  return iterCommercialFieldDiffs(savedDomains, draftDomains, options.restrictDomains);
}

/** Append WordPress-style rows to embedded project change_log JSON. */
export function appendCommercialChangeLogEntries(acfGroupsJson, changes, userEmail) {
  if (!changes?.length) return acfGroupsJson;
  const stub = wpUserStubFromEmail(userEmail);
  const root =
    acfGroupsJson != null && typeof acfGroupsJson === "object" && !Array.isArray(acfGroupsJson)
      ? JSON.parse(JSON.stringify(acfGroupsJson))
      : {};
  if (
    !root[CHANGE_LOG_ACF_GROUP] ||
    typeof root[CHANGE_LOG_ACF_GROUP] !== "object" ||
    Array.isArray(root[CHANGE_LOG_ACF_GROUP])
  ) {
    root[CHANGE_LOG_ACF_GROUP] = {};
  }
  const g = root[CHANGE_LOG_ACF_GROUP];
  const prev = g.change_log;
  const arr = Array.isArray(prev) ? [...prev] : [];
  const now = new Date();
  const dateStr = formatWpChangeLogDate(now);
  for (const { fieldName, oldRaw, newVal } of changes) {
    const oldS = safeWpInnerSnippet(oldRaw);
    const newS = safeWpInnerSnippet(newVal);
    arr.push({
      user: stub,
      field: fieldName,
      value: [`Changed from '${oldS}' to '${newS}'`],
      date: dateStr,
    });
  }
  g.change_log = arr;
  return root;
}

/** PATCH body for commercial domains; drops invalid numbers. */
export function buildCommercialPatchBody(existingAcf, domains, options = {}) {
  let acf_groups_json = mergeCommercialIntoAcfGroups(existingAcf, domains);
  acf_groups_json = applyCommercialSubphasesToAcf(acf_groups_json, domains);
  const syncedSummary = acf_groups_json[COMMERCIAL_DOMAINS.SUMMARY] || {};
  const domainsWithSyncedSummary = {
    ...domains,
    [COMMERCIAL_DOMAINS.SUMMARY]: {
      ...(domains[COMMERCIAL_DOMAINS.SUMMARY] || {}),
      ...syncedSummary,
    },
  };
  const raw = projectScalarsFromCommercialDomains(domainsWithSyncedSummary);
  const scalars = {};
  const alwaysIncludeNull = new Set(["project_notes", "new_customer", "site_walk", "incident"]);
  for (const [k, v] of Object.entries(raw)) {
    if (v === null || v === undefined) {
      if (alwaysIncludeNull.has(k)) scalars[k] = v === undefined ? null : v;
      continue;
    }
    if (typeof v === "number" && !Number.isFinite(v)) continue;
    scalars[k] = v;
  }
  return {
    acf_groups_json,
    ...scalars,
    cvin_expansion: false,
    _source: options.source || "commercial_landing",
  };
}

function coerceProjectBoolLocal(v) {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "boolean") return v;
  const s = String(v).trim().toLowerCase();
  if (["1", "true", "yes", "y"].includes(s)) return true;
  if (["0", "false", "no", "n"].includes(s)) return false;
  return null;
}

/** True for commercial / revenue rows (not CVIN expansion subsegments). */
export function isCommercialProjectRow(row) {
  if (!row || typeof row !== "object") return true;
  if (row.cvin_expansion === true) return false;

  const j = row.acf_groups_json;
  const sub = j?.subsegment_summary;
  if (sub && typeof sub === "object" && !Array.isArray(sub) && Object.keys(sub).length > 0) {
    return false;
  }

  const summary = j?.project_summary || j?.["Project Summary Test 2"] || j?.project_summary_test_2;
  const fromSummary = coerceProjectBoolLocal(summary?.cvin_expansion);
  if (fromSummary === true) return false;
  if (fromSummary === false) return true;

  const col = coerceProjectBoolLocal(row.cvin_expansion);
  if (col === true) return false;
  if (col === false) return true;

  const slug = String(row.export_slug || "").trim().toLowerCase();
  if (slug.startsWith("seg-")) return false;

  return true;
}
